import React, { useState } from "react";
import { Bot, Cpu, Globe, History, Phone, ShieldAlert, Store } from "lucide-react";

import PixelAvatar from "@/shared/components/PixelAvatar";
import SectionNav, { type SectionNavGroup } from "@/shared/components/SectionNav";
import {
  BellIcon, GripIcon, LibraryIcon, LockIcon, ScrollTextIcon, ServerIcon, ShieldCheckIcon, SlidersHorizontalIcon, SparklesIcon, SquareTerminalIcon, UsersIcon,
} from "@/shared/components/icons/animated";
import { useAgentLinks } from "@/shared/api/apps";
import { useKnowledgeBases } from "@/shared/api/knowledge";
import { useAgentSkills } from "@/shared/api/skills";
import { useMcpServers } from "./hooks/useMcpServers";
import { SECTION_HELP, SectionHelpDialog, type ConfigSection } from "./ConfigSectionHelp";
import PublishBar from "./settings/PublishBar";
import GeneralSection from "./settings/GeneralSection";
import ModelSection from "./settings/ModelSection";
import InstructionsSection from "./settings/InstructionsSection";
import GuardrailsSection from "./settings/GuardrailsSection";
import AppsSection from "./settings/AppsSection";
import McpSection from "./settings/McpSection";
import SkillsSection from "./settings/SkillsSection";
import SandboxSection from "./settings/SandboxSection";
import WebSection from "./settings/WebSection";
import VoiceSection from "./settings/VoiceSection";
import SubAgentsSection from "./settings/SubAgentsSection";
import ApprovalSection from "./settings/ApprovalSection";
import NotificationsSection from "./settings/NotificationsSection";
import MarketplaceSection from "./settings/MarketplaceSection";
import KnowledgeSection from "./settings/KnowledgeSection";
import VersionsSection from "./settings/VersionsSection";
import PoliciesSection from "./settings/PoliciesSection";

import type { Agent } from "@/shared/types";

/** The sections, by the name the address carries (`/agents/:agentId/settings/:section`). */
export type AgentSettingsSection =
  | "general" | "model" | "voice" | "instructions" | "guardrails"
  | "apps" | "mcp" | "skills" | "sandbox" | "web" | "sub-agents" | "approval"
  | "knowledge" | "onboarding" | "policies" | "notifications" | "marketplace" | "versions";

/** Sections that lay out a canvas beside a pane, so they take the wider column. */
const WIDE: AgentSettingsSection[] = ["onboarding", "policies"];

/** A count worth showing: nothing for none, and "Off" for a section switched off. */
const meta = (n: number, on = true) => (!on ? "Off" : n || undefined);

/* An agent's whole definition, one section at a time: a sidebar of its sections
   inside the card (the same `SectionNav` as workspace Settings) and the open
   section beside it, under one header that says where the draft stands and
   publishes it. General (icon, name, duplicate, delete), Knowledge, Onboarding, Policies,
   Notifications, Marketplace and Versions apply at once; every other section edits the draft. */
interface Props {
  /** App renders this view only with an agent selected, but the guard below is
      the component's own — so the prop is typed as it is actually passed. */
  agent?: Agent | null;
  occupiedAvatars?: readonly { seed: string; hue: number }[];
  /** The section the address names (`/agents/:agentId/settings/:section`); the page follows it
      when it changes. Any string: a link may name a section this agent doesn't have. */
  section?: string;
  onSectionChange?: (section: AgentSettingsSection) => void;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onDuplicateAgent: () => void;
  onDeleteAgent: () => void;
  /** The Onboarding section, from `features/onboarding`: a feature can't import another, so the
      shell hands it in. */
  onboarding?: React.ReactNode;
}

export default function AgentSettingsPage(props: Props) {
  return props.agent ? <AgentSettings {...props} agent={props.agent} /> : null;
}

function AgentSettings({ agent, occupiedAvatars = [], section: addressed = "general", onSectionChange, onUpdateAgent, onDuplicateAgent, onDeleteAgent, onboarding }: Props & { agent: Agent }) {
  const [requested, setRequested] = useState(addressed);
  // The address moved (Back, a link): the open section follows it.
  const [lastAddressed, setLastAddressed] = useState(addressed);
  if (addressed !== lastAddressed) {
    setLastAddressed(addressed);
    setRequested(addressed);
  }
  const [helpFor, setHelpFor] = useState<ConfigSection | null>(null);
  // Counted for the sidebar; the sections' own lists read the same queries.
  const { data: links } = useAgentLinks(agent.id);
  const { data: mcpServers } = useMcpServers(agent.id);
  const { data: knowledgeBases } = useKnowledgeBases(agent.id);
  const { data: skills } = useAgentSkills(agent.id);

  const caps = agent.capabilities ?? {};
  const label = (section: ConfigSection) => SECTION_HELP[section].title;
  const groups: SectionNavGroup<AgentSettingsSection>[] = [
    {
      label: "Agent",
      items: [
        { id: "general", label: "General", icon: SlidersHorizontalIcon },
        { id: "model", label: label("model"), icon: Cpu },
        { id: "voice", label: label("voice"), icon: Phone, meta: meta(0, caps.voiceEnabled) },
        { id: "instructions", label: label("instructions"), icon: ScrollTextIcon },
        { id: "guardrails", label: label("guardrails"), icon: ShieldAlert, meta: meta(agent.guardrails?.length ?? 0) },
        { id: "onboarding", label: "Onboarding", icon: UsersIcon },
        { id: "policies", label: "Policies", icon: LockIcon },
        { id: "notifications", label: "Notifications", icon: BellIcon },
        { id: "marketplace", label: "Marketplace", icon: Store },
      ],
    },
    {
      label: "Tools",
      items: [
        { id: "apps", label: label("apps"), icon: GripIcon, meta: meta(links?.length ?? 0, caps.appsEnabled) },
        { id: "mcp", label: label("mcp"), icon: ServerIcon, meta: meta(mcpServers?.length ?? 0, caps.mcpEnabled) },
        { id: "skills", label: label("skills"), icon: SparklesIcon, meta: meta(skills?.filter((s) => s.link.status === "attached").length ?? 0) },
        { id: "sandbox", label: label("sandbox"), icon: SquareTerminalIcon, meta: meta(0, caps.sandboxEnabled) },
        { id: "web", label: label("web"), icon: Globe, meta: meta(0, caps.webEnabled) },
        { id: "sub-agents", label: label("subagents"), icon: Bot, meta: meta(agent.subAgents?.length ?? 0, caps.subAgentsEnabled) },
        { id: "approval", label: label("approval"), icon: ShieldCheckIcon, meta: meta(agent.needsApproval?.length ?? 0) },
      ],
    },
    {
      label: "Knowledge",
      items: [
        { id: "knowledge", label: label("kb"), icon: LibraryIcon, meta: meta(knowledgeBases?.length ?? 0) },
      ],
    },
    {
      label: "History",
      items: [{ id: "versions", label: "Versions", icon: History, meta: <span className="font-mono">{agent.version}</span> }],
    },
  ];
  // A section this agent lacks (a stale deep link) opens General.
  const section = groups.flatMap((g) => g.items).find((item) => item.id === requested)?.id ?? "general";
  const select = (next: AgentSettingsSection) => {
    setRequested(next);
    onSectionChange?.(next);
  };

  const props = { agent, onUpdateAgent, onHelp: setHelpFor };
  const pane: Record<AgentSettingsSection, () => React.ReactNode> = {
    general: () => (
      <GeneralSection agent={agent} occupiedAvatars={occupiedAvatars} onUpdateAgent={onUpdateAgent} onDuplicateAgent={onDuplicateAgent} onDeleteAgent={onDeleteAgent} />
    ),
    model: () => <ModelSection {...props} />,
    voice: () => <VoiceSection {...props} />,
    instructions: () => <InstructionsSection {...props} />,
    guardrails: () => <GuardrailsSection {...props} />,
    apps: () => <AppsSection {...props} />,
    mcp: () => <McpSection {...props} />,
    skills: () => <SkillsSection {...props} />,
    sandbox: () => <SandboxSection {...props} />,
    web: () => <WebSection {...props} />,
    "sub-agents": () => <SubAgentsSection {...props} />,
    approval: () => <ApprovalSection {...props} />,
    knowledge: () => <KnowledgeSection {...props} />,
    onboarding: () => onboarding,
    policies: () => <PoliciesSection agent={agent} />,
    notifications: () => <NotificationsSection agent={agent} />,
    marketplace: () => <MarketplaceSection agent={agent} />,
    versions: () => <VersionsSection agent={agent} />,
  };

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col md:flex-row">
      <SectionNav
        title={
          <span className="flex items-center gap-2.5 min-w-0">
            <PixelAvatar seed={agent.seed} hue={agent.hue} size={28} ring rounded={8} />
            <span className="min-w-0">
              <span className="block truncate text-[13.5px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{agent.name}</span>
              <span className="block text-[11.5px] font-normal text-[var(--text-tertiary)]">Settings</span>
            </span>
          </span>
        }
        groups={groups}
        active={section}
        onSelect={select}
      />
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <PublishBar agent={agent} />
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div key={section} className={`${WIDE.includes(section) ? "max-w-[1120px]" : "max-w-[720px]"} mx-auto px-4 md:px-8 pt-6 md:pt-8 pb-16`}>
            {pane[section]()}
          </div>
        </div>
      </div>
      <SectionHelpDialog section={helpFor} onOpenChange={(o) => !o && setHelpFor(null)} />
    </div>
  );
}
